import { useState } from 'react';
import { Check, CircleAlert, Sparkles } from 'lucide-react';
import { getEventModuleLabel } from '@conference/shared';
import type { EventBuilderDraft, EventBuilderTemplate } from '../services/eventBuilderService';
import EventBuilderTemplateComparison from './EventBuilderTemplateComparison';
import { Button } from './ui';

const requirements = [
  ['name', 'Event name'], ['date_start', 'Start date'], ['date_end', 'End date'],
  ['timezone', 'Timezone'], ['location', 'Location'], ['access', 'Access'],
] as const;

export function builderDraftIsReady(draft: EventBuilderDraft | null): boolean {
  if (!draft || draft.status !== 'active' || draft.template_state.mode === 'discovery') return false;
  const value = draft.requirements;
  return Boolean(
    value.name && value.date_start && value.date_end && value.timezone && value.access
    && (value.online === true || value.location)
    && Object.values(draft.blueprint.app_modules).some((module) => module.enabled === true),
  );
}

export default function EventBuilderProposal({
  draft, templates, converting, templateBusy,
  onPreviewTemplate, onCustomize, onRecommendNow, onCreate,
}: {
  draft: EventBuilderDraft | null;
  templates: EventBuilderTemplate[];
  converting: boolean;
  templateBusy: boolean;
  onPreviewTemplate: (key: string) => void;
  onCustomize: () => void;
  onRecommendNow: () => void;
  onCreate: () => void;
}) {
  const [comparing, setComparing] = useState(false);
  const recommendation = draft?.template_state.recommended_template;
  const selected = draft?.template_state.selected_template;
  const selectedTemplate = templates.find((item) => item.key === selected?.key);
  const recommendedTemplate = templates.find((item) => item.key === recommendation?.key);
  const enabledModules = Object.entries(draft?.blueprint.app_modules ?? {}).filter(([, value]) => value.enabled === true);
  const moduleOverrides = (draft?.template_state.overrides.app_modules ?? {}) as Record<string, { enabled?: boolean }>;

  return <section aria-label="Event proposal" className="space-y-4 rounded-2xl border border-gray-800 bg-gray-900 p-5">
    <div>
      <h2 className="font-semibold text-white">Proposal</h2>
      <p className="mt-1 text-xs text-gray-500">Choose the attendee experience before creating the editable event draft.</p>
    </div>

    {draft?.template_state.mode === 'discovery' && recommendation && recommendedTemplate ? <div className="rounded-xl border border-sky-500/40 bg-sky-500/5 p-4">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-300"><Sparkles size={14} /> Recommended starting point</p>
      <h3 className="mt-3 text-lg font-semibold text-white">{recommendedTemplate.name}</h3>
      <p className="mt-1 text-sm leading-6 text-gray-300">{recommendation.reason}</p>
      {!!draft.template_state.understanding.length && <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">What I understand</p>
        <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-gray-400">{draft.template_state.understanding.map((item) => <li key={item}>{item}</li>)}</ul>
      </div>}
      {!!recommendation.assumptions.length && <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Assumptions to review</p>
        <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-gray-400">{recommendation.assumptions.map((item) => <li key={item}>{item}</li>)}</ul>
      </div>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button loading={templateBusy} onClick={() => onPreviewTemplate(recommendation.key)}>Preview template</Button>
        <Button variant="secondary" disabled={templateBusy} onClick={() => setComparing(true)}>Compare templates</Button>
        <Button variant="ghost" disabled={templateBusy} onClick={onCustomize}>Customize with AI</Button>
      </div>
    </div> : draft?.template_state.mode === 'discovery' ? <div className="rounded-xl border border-gray-800 bg-gray-950 p-4">
      <p className="text-sm font-medium text-white">Shape the event with the AI consultant</p>
      <p className="mt-1 text-sm text-gray-400">Answer a few focused questions, request a recommendation now, or choose a starting point yourself.</p>
      {!!draft.template_state.understanding.length && <ul className="mt-3 list-disc space-y-1 pl-4 text-sm text-gray-400">{draft.template_state.understanding.map((item) => <li key={item}>{item}</li>)}</ul>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button loading={templateBusy} onClick={onRecommendNow}>Recommend now</Button>
        <Button variant="secondary" disabled={templateBusy} onClick={() => setComparing(true)}>Browse templates</Button>
        <Button variant="ghost" disabled={templateBusy} onClick={onCustomize}>Customize with AI</Button>
      </div>
    </div> : <div className="rounded-xl border border-gray-800 bg-gray-950 p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Current design</p>
      <h3 className="mt-2 font-semibold text-white">{selectedTemplate?.name ?? 'Custom AI design'}</h3>
      <p className="mt-1 text-sm text-gray-400">{selectedTemplate
        ? `Template version ${selected?.version}. Explicit changes stay in place when you switch templates.`
        : 'Use the conversation to adjust modules and attendee destinations.'}</p>
      {!!selectedTemplate && <div className="mt-3 flex flex-wrap gap-1.5 text-xs text-sky-200">
        {selectedTemplate.destinations.map((item) => <span key={item} className="rounded-full bg-sky-500/10 px-2.5 py-1">{item}</span>)}
      </div>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="secondary" disabled={templateBusy} onClick={() => setComparing(true)}>Change template</Button>
        {selectedTemplate && <Button variant="ghost" loading={templateBusy} onClick={onCustomize}>Customize with AI</Button>}
      </div>
    </div>}

    <div className="space-y-2">
      {requirements.map(([key, label]) => {
        const value = key === 'location' && draft?.requirements.online === true ? 'Online' : draft?.requirements[key];
        return <div key={key} className="flex items-start gap-2 rounded-lg bg-gray-950 px-3 py-2.5 text-sm">
          {value ? <Check size={15} className="mt-0.5 text-emerald-400" /> : <CircleAlert size={15} className="mt-0.5 text-amber-400" />}
          <div><p className="text-xs text-gray-500">{label}</p><p className="text-gray-200">{value ? String(value) : 'Missing'}</p></div>
        </div>;
      })}
    </div>
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Enabled modules</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {enabledModules.length ? enabledModules.map(([key, value]) => <span key={key} className="rounded-full bg-sky-500/10 px-2.5 py-1 text-xs text-sky-300">
          {value.label || getEventModuleLabel(undefined, key as never)}
          {selectedTemplate && <span className="ml-1 text-gray-500">· {key in moduleOverrides ? 'custom' : 'template'}</span>}
        </span>) : <span className="text-sm text-gray-500">None yet</span>}
      </div>
    </div>
    {!!draft?.assumptions.length && <div><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Working assumptions</p><p className="mt-1 text-xs text-gray-600">Unconfirmed inferences stay here until you confirm or correct them.</p><ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-gray-400">{draft.assumptions.map((item) => <li key={item}>{item}</li>)}</ul></div>}
    {!!draft?.unsupported_requests.length && <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4"><p className="text-sm font-medium text-amber-200">Not currently supported</p><ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-amber-100/80">{draft.unsupported_requests.map((item) => <li key={item}>{item}</li>)}</ul></div>}
    {!!draft?.uploads.length && <div><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Staged files</p><ul className="mt-2 space-y-1 text-sm text-gray-400">{draft.uploads.map((upload) => <li key={upload.id}>{upload.filename} · {upload.kind}</li>)}</ul></div>}
    <Button className="w-full justify-center" loading={converting} disabled={!builderDraftIsReady(draft)} onClick={onCreate}>{draft?.created_event_id ? 'Apply changes' : 'Create event'}</Button>

    <EventBuilderTemplateComparison
      open={comparing}
      templates={templates}
      selectedKey={selected?.key ?? null}
      onClose={() => setComparing(false)}
      onSelect={(key) => { setComparing(false); onPreviewTemplate(key); }}
    />
  </section>;
}
